import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  title: "", subtitle: "", image_url: "", image_mobile: "", cta_text: "Ver Ofertas",
  cta_link: "/produtos", slot: "promo", show_avatar: false, full_image: false,
  active: true, order: 0,
};

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = () => api.get("/admin/banners").then((r) => setBanners(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...EMPTY, ...b }); setDialogOpen(true); };

  const uploadFile = async (file, field = "image_url") => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, [field]: data.url }));
      toast.success("Imagem enviada!");
    } catch (err) {
      toast.error(formatApiError(err, "Falha no upload."));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Informe o título do banner.");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/banners/${editing.id}`, form);
        toast.success("Banner atualizado!");
      } else {
        await api.post("/admin/banners", form);
        toast.success("Banner criado!");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/banners/${deleting.id}`);
      toast.success("Banner excluído.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-banners">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Banners</h1>
          <p className="text-sm text-gray-500 mt-1">Banners da página inicial e áreas promocionais</p>
        </div>
        <Button onClick={openCreate} className="rounded-full" data-testid="banner-create-button">
          <Plus className="w-4 h-4" /> Novo Banner
        </Button>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {banners.length === 0 && (
          <p className="text-sm text-slate-400 col-span-2 bg-white border border-slate-200 rounded-xl p-8 text-center">
            Nenhum banner cadastrado.
          </p>
        )}
        {banners.map((b) => (
          <div key={b.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`admin-banner-card-${b.id}`}>
            <div className="relative h-36 bg-slate-100">
              {b.image_url && <img src={b.image_url} alt="" className="w-full h-full object-cover" />}
              <span className={`absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${b.slot === "hero" ? "bg-brand text-white" : "bg-orange-500 text-white"}`}>
                {b.slot === "hero" ? "Principal (Hero)" : "Promocional"}
              </span>
              {b.show_avatar && (
                <span className="absolute top-2 right-2 text-[10px] font-bold bg-white/90 text-gray-700 rounded-full px-2 py-0.5">
                  Com mascote
                </span>
              )}
            </div>
            <div className="p-4">
              <p className="font-semibold text-gray-900 truncate">{b.title}</p>
              <p className="text-xs text-slate-500 truncate mt-0.5">{b.subtitle}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${b.active ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                  {b.active ? "Ativo" : "Inativo"}
                </span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(b)} data-testid={`banner-edit-${b.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-red-500" onClick={() => setDeleting(b)} data-testid={`banner-delete-${b.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="banner-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Banner" : "Novo Banner"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div>
              <Label>Subtítulo</Label>
              <Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="mt-1.5" data-testid="banner-form-subtitle" />
            </div>
            <div>
              <Label>Aviso em letras miúdas (opcional — ex.: condições da entrega em 24h)</Label>
              <Input value={form.disclaimer || ""} onChange={(e) => setForm({ ...form, disclaimer: e.target.value })} className="mt-1.5" placeholder="*Sujeito à disponibilidade do produto e à região atendida." data-testid="banner-form-disclaimer" />
            </div>
            <div>
              <Label>Título (dica: use | para destacar a segunda parte em amarelo no banner principal)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="Entrega em até|24 Horas*" required data-testid="banner-form-title" />
            </div>
            <div>
              <Label>Imagem de fundo (computador)</Label>
              <div className="mt-1.5 flex gap-2">
                <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="URL da imagem ou faça upload" className="h-9 text-sm" data-testid="banner-form-image" />
                <label className="shrink-0 inline-flex items-center gap-1.5 border border-slate-200 rounded-md px-3 text-sm text-slate-600 hover:border-brand hover:text-brand cursor-pointer transition-colors" data-testid="banner-image-upload">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0])} />
                </label>
              </div>
            </div>
            <div>
              <Label>Imagem para celular (opcional — vazio = usa a mesma)</Label>
              <div className="mt-1.5 flex gap-2">
                <Input value={form.image_mobile || ""} onChange={(e) => setForm({ ...form, image_mobile: e.target.value })} placeholder="Arte na vertical, ex.: 1080x1350" className="h-9 text-sm" data-testid="banner-form-image-mobile" />
                <label className="shrink-0 inline-flex items-center gap-1.5 border border-slate-200 rounded-md px-3 text-sm text-slate-600 hover:border-brand hover:text-brand cursor-pointer transition-colors" data-testid="banner-image-mobile-upload">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "image_mobile")} />
                </label>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Com arte própria para celular, o banner troca sozinho conforme a tela — sem cortes.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Texto do botão</Label>
                <Input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} className="mt-1.5" data-testid="banner-form-cta" />
              </div>
              <div>
                <Label>Link do botão</Label>
                <Input value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} className="mt-1.5" placeholder="/produtos?promo=1" data-testid="banner-form-link" />
              </div>
              <div>
                <Label>Posição</Label>
                <Select value={form.slot} onValueChange={(v) => setForm({ ...form, slot: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="banner-form-slot">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hero">Principal (topo da home)</SelectItem>
                    <SelectItem value="promo">Promocional (meio da home)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ordem</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })} className="mt-1.5" data-testid="banner-form-order" />
              </div>
            </div>
            <div className="flex items-center gap-6 flex-wrap">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Switch checked={form.show_avatar} onCheckedChange={(v) => setForm({ ...form, show_avatar: v })} data-testid="banner-form-avatar" />
                Mostrar mascote HG
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Switch checked={form.full_image} onCheckedChange={(v) => setForm({ ...form, full_image: v })} data-testid="banner-form-full-image" />
                Imagem completa (arte pronta, sem textos sobrepostos)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="banner-form-active" />
                Ativo
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="banner-form-save">
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir banner?</AlertDialogTitle>
            <AlertDialogDescription>"{deleting?.title}" será removido da página inicial.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="banner-delete-confirm">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
